/**
 * Custom Dropdown for CF7 <select> elements
 *
 * Replaces native <select> inside .contact-form with a styled dropdown
 * while keeping the original <select> hidden for form submission.
 *
 * CF7 shortcodes should use first_as_label for the placeholder text:
 *   [select building first_as_label "Select a building"]
 *   [select room first_as_label "Select a room" "Single" "Double"]
 */

const CHEVRON_SVG = `<svg class="cf7-dropdown__chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" fill="currentColor"/></svg>`;

function buildDropdown( select ) {
	const wrapper = document.createElement( 'div' );
	wrapper.className = 'cf7-dropdown';

	const options = Array.from( select.options );

	// Detect the placeholder: first option with value="" OR first_as_label text
	const firstOpt = options[ 0 ];
	const isPlaceholder = ( opt ) => opt.value === '' || opt.value === opt.textContent.trim();
	const placeholderText = firstOpt ? firstOpt.textContent.trim() : 'Select';

	// Use first_as_label text if present, otherwise fallback
	const displayPlaceholder = ( firstOpt && firstOpt.value === '' )
		? placeholderText.replace( /^—|—$/g, '' ).trim() || placeholderText
		: placeholderText;

	// Trigger button
	const trigger = document.createElement( 'button' );
	trigger.type = 'button';
	trigger.className = 'cf7-dropdown__trigger';
	trigger.setAttribute( 'aria-haspopup', 'listbox' );
	trigger.setAttribute( 'aria-expanded', 'false' );

	const triggerText = document.createElement( 'span' );
	triggerText.className = 'cf7-dropdown__trigger-text';

	trigger.appendChild( triggerText );
	trigger.insertAdjacentHTML( 'beforeend', CHEVRON_SVG );

	// Options panel — skip placeholder options
	const panel = document.createElement( 'div' );
	panel.className = 'cf7-dropdown__panel';
	panel.setAttribute( 'role', 'listbox' );

	options.forEach( ( opt ) => {
		// Skip blank placeholders and first_as_label placeholders
		if ( opt.value === '' ) return;
		if ( opt === firstOpt && opt.value === opt.textContent.trim() ) return;

		const item = document.createElement( 'button' );
		item.type = 'button';
		item.className = 'cf7-dropdown__option';
		item.setAttribute( 'role', 'option' );
		item.setAttribute( 'data-value', opt.value );
		item.textContent = opt.textContent;
		panel.appendChild( item );
	} );

	// Set initial display text
	const currentSelected = select.options[ select.selectedIndex ];
	const hasRealValue = currentSelected && currentSelected.value !== '' &&
		!( currentSelected === firstOpt && isPlaceholder( currentSelected ) );

	triggerText.textContent = hasRealValue ? currentSelected.textContent : displayPlaceholder;

	if ( ! hasRealValue ) {
		wrapper.classList.add( 'is-placeholder' );
	}

	// Store placeholder for reset
	wrapper.dataset.placeholder = displayPlaceholder;

	// Assemble — insert wrapper where the select is
	select.parentNode.insertBefore( wrapper, select );
	wrapper.appendChild( trigger );
	wrapper.appendChild( panel );
	wrapper.appendChild( select );
	select.setAttribute( 'tabindex', '-1' );
	select.setAttribute( 'aria-hidden', 'true' );
	select.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);border:0;';

	// If this is a "room" select and there's a sibling "building" select,
	// start disabled until a building is chosen.
	if ( select.name === 'room' ) {
		const form = select.closest( 'form' );
		const hasBuildingSelect = form?.querySelector( 'select[name="building"]' );
		if ( hasBuildingSelect ) {
			wrapper.classList.add( 'is-disabled' );
			trigger.setAttribute( 'aria-disabled', 'true' );
		}
	}

	// -- Events --
	trigger.addEventListener( 'click', () => {
		if ( wrapper.classList.contains( 'is-disabled' ) ) return;
		toggle( wrapper );
	} );

	panel.addEventListener( 'click', ( e ) => {
		const opt = e.target.closest( '.cf7-dropdown__option' );
		if ( ! opt ) return;

		const value = opt.getAttribute( 'data-value' );
		select.value = value;
		select.dispatchEvent( new Event( 'change', { bubbles: true } ) );

		triggerText.textContent = opt.textContent;
		wrapper.classList.remove( 'is-placeholder' );

		// Mark active
		panel.querySelectorAll( '.cf7-dropdown__option' ).forEach( ( o ) => o.classList.remove( 'is-selected' ) );
		opt.classList.add( 'is-selected' );

		close( wrapper );
	} );

	// Keyboard navigation
	wrapper.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'Escape' ) {
			close( wrapper );
			trigger.focus();
		}

		if ( e.key === 'ArrowDown' || e.key === 'ArrowUp' ) {
			e.preventDefault();
			const items = Array.from( panel.querySelectorAll( '.cf7-dropdown__option' ) );
			const focused = panel.querySelector( '.cf7-dropdown__option:focus' );
			let idx = items.indexOf( focused );

			if ( e.key === 'ArrowDown' ) idx = Math.min( idx + 1, items.length - 1 );
			else idx = Math.max( idx - 1, 0 );

			items[ idx ]?.focus();
		}
	} );

	// Reset on CF7 form reset (after successful submission)
	select.closest( 'form' )?.addEventListener( 'reset', () => {
		setTimeout( () => {
			triggerText.textContent = displayPlaceholder;
			wrapper.classList.add( 'is-placeholder' );
			panel.querySelectorAll( '.cf7-dropdown__option' ).forEach( ( o ) => o.classList.remove( 'is-selected' ) );
		}, 0 );
	} );
}

function toggle( wrapper ) {
	const isOpen = wrapper.classList.contains( 'is-open' );
	document.querySelectorAll( '.cf7-dropdown.is-open' ).forEach( ( d ) => close( d ) );
	if ( ! isOpen ) open( wrapper );
}

function open( wrapper ) {
	wrapper.classList.add( 'is-open' );
	wrapper.querySelector( '.cf7-dropdown__trigger' ).setAttribute( 'aria-expanded', 'true' );
	const first = wrapper.querySelector( '.cf7-dropdown__option' );
	first?.focus();
}

function close( wrapper ) {
	wrapper.classList.remove( 'is-open' );
	wrapper.querySelector( '.cf7-dropdown__trigger' ).setAttribute( 'aria-expanded', 'false' );
}

// Close on outside click
document.addEventListener( 'click', ( e ) => {
	if ( ! e.target.closest( '.cf7-dropdown' ) ) {
		document.querySelectorAll( '.cf7-dropdown.is-open' ).forEach( ( d ) => close( d ) );
	}
} );

/**
 * Cascading: when a "building" dropdown value changes,
 * fetch room types for that residence and update the "room" dropdown.
 */
function initCascading() {
	const config = window.thriveResidences;
	if ( ! config?.map ) return;

	// Listen for building select changes.
	document.querySelectorAll( 'select[name="building"]' ).forEach( ( buildingSelect ) => {
		buildingSelect.addEventListener( 'change', async () => {
			const title = buildingSelect.value;
			const postId = config.map[ title ];

			// Find the sibling room dropdown in the same form.
			const form = buildingSelect.closest( 'form' );
			const roomSelect = form?.querySelector( 'select[name="room"]' );
			if ( ! roomSelect ) return;

			const roomWrapper = roomSelect.closest( '.cf7-dropdown' );
			if ( ! roomWrapper ) return;

			const panel = roomWrapper.querySelector( '.cf7-dropdown__panel' );
			const triggerText = roomWrapper.querySelector( '.cf7-dropdown__trigger-text' );
			const placeholder = roomWrapper.dataset.placeholder || 'Select a room';

			// Reset room dropdown.
			panel.innerHTML = '';
			roomSelect.innerHTML = `<option value="">${ placeholder }</option>`;
			triggerText.textContent = placeholder;
			roomWrapper.classList.add( 'is-placeholder' );

			if ( ! postId ) {
				// No building selected — disable room.
				roomWrapper.classList.add( 'is-disabled' );
				roomWrapper.querySelector( '.cf7-dropdown__trigger' )?.setAttribute( 'aria-disabled', 'true' );
				return;
			}

			// Fetch rooms from REST API.
			try {
				const res = await fetch( `${ config.restUrl }/${ postId }/rooms`, {
					headers: { 'X-WP-Nonce': config.nonce },
				} );
				const rooms = await res.json();

				if ( ! Array.isArray( rooms ) || rooms.length === 0 ) return;

				rooms.forEach( ( room ) => {
					const optionLabel = room.variant_name || room.name;
					if ( ! optionLabel ) return;

					// Update hidden select.
					const opt = document.createElement( 'option' );
					opt.value = optionLabel;
					opt.textContent = optionLabel;
					roomSelect.appendChild( opt );

					// Update custom dropdown panel.
					const btn = document.createElement( 'button' );
					btn.type = 'button';
					btn.className = 'cf7-dropdown__option';
					btn.setAttribute( 'role', 'option' );
					btn.setAttribute( 'data-value', optionLabel );
					btn.textContent = optionLabel;
					panel.appendChild( btn );
				} );

				// Enable room dropdown.
				roomWrapper.classList.remove( 'is-disabled' );
				roomWrapper.querySelector( '.cf7-dropdown__trigger' )?.removeAttribute( 'aria-disabled' );
			} catch ( err ) {
				// Silently fail — room dropdown stays empty.
			}
		} );
	} );
}

/**
 * Wrap CF7 submit inputs in a span so we can apply ::before hover effect.
 * <input> elements don't support pseudo-elements.
 */
function wrapSubmitButtons() {
	document.querySelectorAll( '.contact-page__form input[type="submit"].wpcf7-form-control' ).forEach( ( input ) => {
		if ( input.parentElement?.classList.contains( 'cf7-submit' ) ) return;
		const wrapper = document.createElement( 'span' );
		wrapper.className = 'cf7-submit';
		input.parentNode.insertBefore( wrapper, input );
		wrapper.appendChild( input );
	} );
}

export const Cf7Dropdown = {
	init() {
		document.querySelectorAll( '.contact-page__form select.wpcf7-form-control' ).forEach( ( select ) => {
			buildDropdown( select );
		} );

		wrapSubmitButtons();
		initCascading();
	},
};
